import { useState, useEffect, useCallback } from 'react';
import { useLang } from '../contexts/LangContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Wallet, Upload, Loader2, ImageIcon, X, Eye } from 'lucide-react';
import TablePagination from '../components/TablePagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function WithdrawalPage() {
  const { t } = useLang();
  const [withdrawals, setWithdrawals] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 1 });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [form, setForm] = useState({ employee_id: '', employee_name: '', amount: 0, notes: '', transfer_proof_url: '' });

  const loadData = useCallback(async (page = 1) => {
    try {
      const [wRes, eRes] = await Promise.all([api.listWithdrawals(page, 20), api.listAllEmployees()]);
      setWithdrawals(wRes.data.data);
      setPagination({ total: wRes.data.total, page: wRes.data.page, limit: wRes.data.limit, total_pages: wRes.data.total_pages });
      setEmployees(eRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const emp = employees.find(em => em.id === form.employee_id);
      await api.createWithdrawal({
        ...form,
        employee_name: emp?.name || form.employee_name,
        amount: Number(form.amount)
      });
      toast.success(t('success'));
      setDialogOpen(false);
      setForm({ employee_id: '', employee_name: '', amount: 0, notes: '', transfer_proof_url: '' });
      loadData(pagination.page);
    } catch (err) { toast.error(err?.response?.data?.detail || t('error')); }
  };

  const handleDelete = async () => {
    try { await api.deleteWithdrawal(deleteId); toast.success(t('success')); setDeleteId(null); loadData(pagination.page); }
    catch (err) { toast.error(t('error')); }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Hanya file gambar'); return; }
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      setForm(prev => ({ ...prev, transfer_proof_url: res.data.url }));
      toast.success('Bukti transfer berhasil diupload');
    } catch (err) { toast.error('Gagal upload'); }
    finally { setUploading(false); }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val || 0);
  const totalWithdrawals = withdrawals.reduce((sum, w) => sum + (w.amount || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="withdrawal-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('withdrawals')}</h1>
          <p className="text-sm text-[#64584F]">{pagination.total} {t('withdrawals').toLowerCase()}</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="add-withdrawal-btn">
          <Plus size={16} className="mr-2" /> {t('add')} {t('withdrawals')}
        </Button>
      </div>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm stat-accent-orange">
        <div className="p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#EB7B44]/10 flex items-center justify-center">
            <Wallet size={24} className="text-[#EB7B44]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs font-medium text-[#64584F] uppercase tracking-wider">Total {t('withdrawals')}</p>
            <p className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }} data-testid="total-withdrawals">{formatCurrency(totalWithdrawals)}</p>
          </div>
        </div>
      </Card>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('employee_name')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('amount')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden sm:table-cell">Bukti Transfer</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">{t('notes')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden lg:table-cell">{t('date')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#A6A2A0]">{t('loading')}</TableCell></TableRow>
            ) : withdrawals.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#A6A2A0]">{t('no_data')}</TableCell></TableRow>
            ) : withdrawals.map(w => (
              <TableRow key={w.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`withdrawal-row-${w.id}`}>
                <TableCell className="font-medium text-[#202020]">{w.employee_name}</TableCell>
                <TableCell className="text-[#EB7B44] font-semibold">{formatCurrency(w.amount)}</TableCell>
                <TableCell className="hidden sm:table-cell">
                  {w.transfer_proof_url ? (
                    <button onClick={() => setPreviewImg(imgSrc(w.transfer_proof_url))} className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`view-proof-${w.id}`}>
                      <Eye size={14} /> Lihat
                    </button>
                  ) : <span className="text-[#CACACC] text-xs">-</span>}
                </TableCell>
                <TableCell className="text-[#64584F] hidden md:table-cell">{w.notes || '-'}</TableCell>
                <TableCell className="text-[#64584F] text-sm hidden lg:table-cell">{w.created_at?.split('T')[0]}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setDeleteId(w.id)}>
                    <Trash2 size={15} className="text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} limit={pagination.limit} onPageChange={(p) => loadData(p)} />
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-md">
          <DialogHeader><DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('add')} {t('withdrawals')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('employees')}</Label>
              <Select value={form.employee_id} onValueChange={val => setForm({...form, employee_id: val})}>
                <SelectTrigger className="border-[#CACACC]" data-testid="withdrawal-employee-select"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} ({e.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('amount')}</Label>
              <Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required className="border-[#CACACC]" data-testid="withdrawal-amount-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('notes')}</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="border-[#CACACC]" data-testid="withdrawal-notes-input" />
            </div>

            {/* Transfer Proof */}
            <div className="space-y-2">
              <Label className="text-[#202020] font-semibold">Bukti Transfer</Label>
              <div className="flex items-start gap-3">
                {form.transfer_proof_url ? (
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#CACACC] bg-[#F6F5F5] flex-shrink-0">
                    <img src={imgSrc(form.transfer_proof_url)} alt="Bukti" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm({...form, transfer_proof_url: ''})} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X size={10} /></button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-lg border-2 border-dashed border-[#CACACC] bg-[#F6F5F5] flex items-center justify-center flex-shrink-0">
                    <ImageIcon size={20} className="text-[#A6A2A0]" />
                  </div>
                )}
                <div className="flex-1">
                  <label className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#CACACC] bg-white cursor-pointer hover:bg-[#F6F5F5] transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`} data-testid="withdrawal-proof-upload-btn">
                    {uploading ? <Loader2 size={14} className="text-[#EB7B44] animate-spin" /> : <Upload size={14} className="text-[#64584F]" />}
                    <span className="text-xs text-[#64584F] font-medium">{uploading ? 'Uploading...' : 'Upload Bukti'}</span>
                    <input type="file" accept="image/*" onChange={handleProofUpload} className="hidden" />
                  </label>
                  <p className="text-[10px] text-[#A6A2A0] mt-1">JPG, PNG. Maks 5MB</p>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#CACACC]">{t('cancel')}</Button>
              <Button type="submit" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="withdrawal-save-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-sm">
          <DialogHeader><DialogTitle className="text-[#202020]">{t('confirm_delete')}</DialogTitle></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#CACACC]">{t('cancel')}</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-withdrawal-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview */}
      <Dialog open={!!previewImg} onOpenChange={() => setPreviewImg(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-lg p-2">
          {previewImg && <img src={previewImg} alt="Bukti Transfer" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
